import { useEffect, useRef, useState, useCallback } from 'react';
import { useAppSelector } from '../store';
import { wsService } from '../services/websocket';
import { selectConnectionStatus } from '../store/slices/realtimeSlice';

interface UseWebSocketOptions {
  autoConnect?: boolean;
  channels?: string[];
  reconnectOnError?: boolean;
}

export const useWebSocket = (
  facilityId: string,
  options: UseWebSocketOptions = {}
) => {
  const {
    autoConnect = true,
    channels = ['worker_updates', 'alerts', 'sensor_status'],
  } = options;

  const connectionStatus = useAppSelector(selectConnectionStatus);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Security fix C6: the JWT is no longer in Redux/localStorage. Trigger
  // (re)connect off the auth flag instead — wsService mints its own ws-token
  // via the httpOnly cookie when connect() is called.
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const wsServiceRef = useRef(wsService);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (autoConnect && facilityId && isAuthenticated) {
      void wsServiceRef.current.connect(facilityId);

      if (channels.length > 0) {
        wsServiceRef.current.subscribe(channels);
      }
    }

    // Don't disconnect on cleanup - let the Layout manage the WebSocket lifecycle
    // This prevents disconnection when navigating between pages
  }, [facilityId, isAuthenticated, autoConnect]);

  useEffect(() => {
    if (!autoConnect) clearReconnectTimer();
    return clearReconnectTimer;
  }, [autoConnect, clearReconnectTimer]);

  useEffect(() => {
    setIsConnected(connectionStatus.connectionStatus === 'connected');
    setError(connectionStatus.error || null);
  }, [connectionStatus]);

  const connect = useCallback(() => {
    if (facilityId && isAuthenticated) {
      void wsServiceRef.current.connect(facilityId);
    }
  }, [facilityId, isAuthenticated]);

  const disconnect = useCallback(() => {
    clearReconnectTimer();
    wsServiceRef.current.disconnect();
  }, [clearReconnectTimer]);

  const reconnect = useCallback(() => {
    clearReconnectTimer();
    wsServiceRef.current.disconnect();
    if (!autoConnect) return;

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      if (autoConnect && facilityId && isAuthenticated) {
        void wsServiceRef.current.connect(facilityId);
      }
    }, 1000);
  }, [autoConnect, clearReconnectTimer, facilityId, isAuthenticated]);

  const subscribe = useCallback((newChannels: string | string[]) => {
    const channelsArray = Array.isArray(newChannels) ? newChannels : [newChannels];
    wsServiceRef.current.subscribe(channelsArray);
  }, []);

  const unsubscribe = useCallback((removeChannels: string | string[]) => {
    const channelsArray = Array.isArray(removeChannels) ? removeChannels : [removeChannels];
    wsServiceRef.current.unsubscribe(channelsArray);
  }, []);

  const sendMessage = useCallback((event: string, data: any) => {
    return wsServiceRef.current.sendMessage(event, data);
  }, []);

  return {
    isConnected,
    connectionStatus: connectionStatus.connectionStatus,
    error,
    connect,
    disconnect,
    reconnect,
    subscribe,
    unsubscribe,
    sendMessage,
  };
};
